import { ErrorHandler, NgModule, inject, provideAppInitializer } from "@angular/core";
import { AppComponent } from "./app.component";
import { BrowserModule, DomSanitizer } from "@angular/platform-browser";
import { provideAnimationsAsync } from "@angular/platform-browser/animations/async";
import { AppRoutingModule } from "./app-routing.module";
import { Store, StoreModule } from "@ngrx/store";
import { effects, reducers, metaReducers } from "./store";
import { environment } from "../environments/environment";
import { EffectsModule } from "@ngrx/effects";
import { StoreDevtoolsModule } from "@ngrx/store-devtools";
import { StoreRouterConnectingModule } from "@ngrx/router-store";
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from "@angular/common/http";
import { ApiModule, Configuration, ProgramService } from "./services/api";
import { initializeApp as appInitializer } from "./app.initializer.factory";
import { registerAppIcons } from "./app.icons";
import { loadInitialTranslations } from "./app.translations";
import { MatIconRegistry } from "@angular/material/icon";
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from "@angular/material/form-field";
import { FORM_FIELD_DEFAULT_OPTIONS } from "./app.form-field";
import { AppInterceptor } from "./interceptors/app/app.interceptor";
import { LanguageInterceptor } from "./interceptors/language/language.interceptor";
import { VersionInterceptor } from "./interceptors/version/version.interceptor";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { provideTranslateHttpLoader } from "@ngx-translate/http-loader";
import { DEFAULT_LANGUAGE } from "./constants";
import * as Sentry from "@sentry/angular";
import { ShopifyInterceptor } from "./interceptors/auth/shopify.interceptor";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { TimezoneInterceptor } from "./interceptors/timezone/timezone.interceptor";
import { ShopifyAppBridgeService } from "./services/shopify-app-bridge/shopify-app-bridge.service";
import { Router } from "@angular/router";
import { ConvercusInterceptor } from "./interceptors/auth/convercus.interceptor";
import { PaymentRequiredInterceptor } from "./interceptors/payment-required/payment-required.interceptor";
import { ServerDownInterceptor } from "./interceptors/server-down/server-down.interceptor";
import { ServerDownComponentModule } from "./components/server-down/server-down.module";

export const setApiConfig = (): Configuration => new Configuration({ basePath: environment.apiUrl });

export function sentryErrorExtractor(error: unknown, defaultExtractor: (error: unknown) => unknown): unknown {
  const extracted = defaultExtractor(error);
  if (extracted) {
    return extracted;
  }

  const candidate = error as Record<string, unknown> | undefined;
  const nested =
    candidate?.['originalError'] ??
    candidate?.['ngOriginalError'] ??
    candidate?.['rejection'] ??
    candidate?.['error'];
  if (nested) {
    const nestedExtracted = defaultExtractor(nested);
    if (nestedExtracted) {
      return nestedExtracted;
    }
  }

  if (error instanceof ErrorEvent) {
    return error.error ?? new Error(error.message);
  }

  if (typeof error === 'object' && error !== null) {
    const obj = error as Record<string, unknown>;
    const message = obj['message'] ?? obj['reason'] ?? obj['statusText'];
    const name = error.constructor?.name ?? 'Unknown';
    let description: string;
    if (typeof message === 'string') {
      description = message;
    } else if (message != null) {
      description = JSON.stringify(message);
    } else {
      try { description = JSON.stringify(error); } catch { description = Object.prototype.toString.call(error); }
    }
    const syntheticError = new Error(`[${name}] ${description}`);
    if (typeof obj['stack'] === 'string') {
      syntheticError.stack = obj['stack'];
    }
    return syntheticError;
  }

  return new Error(`Unhandled non-Error value: ${JSON.stringify(error)}`);
}

Sentry.init({
  release: `jeripass-admin-app@${environment.release}`,
  dsn: "https://hCuFBCEZxc7ecT3JP8XGPPbY@s1613594.eu-nbg-2.betterstackdata.com/1",
  environment: environment.name,
  attachStacktrace: true,
  enabled: ["staging", "production"].includes(environment.name),
  tracePropagationTargets: [environment.apiUrl, environment.webAppUrl],
});

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [BrowserModule,
    AppRoutingModule,
    StoreModule.forRoot(reducers, { metaReducers }),
    EffectsModule.forRoot(effects),
    environment.production ? [] : StoreDevtoolsModule.instrument({ maxAge: 25 }),
    StoreRouterConnectingModule.forRoot(),
    TranslateModule.forRoot({
      fallbackLang: DEFAULT_LANGUAGE,
    }),
    ApiModule.forRoot(setApiConfig),
    MatProgressSpinnerModule,
    ServerDownComponentModule
  ],
  providers: [
    // The animation engine downloads only if a component declares Angular animations (none does today), never up front
    provideAnimationsAsync(),
    { provide: HTTP_INTERCEPTORS, useClass: AppInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: LanguageInterceptor, multi: true },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({ extractor: sentryErrorExtractor }),
    },
    { provide: HTTP_INTERCEPTORS, useClass: VersionInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ShopifyInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ConvercusInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: TimezoneInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: PaymentRequiredInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ServerDownInterceptor, multi: true },
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: FORM_FIELD_DEFAULT_OPTIONS },
    provideAppInitializer(() => {
      const initializerFn = (
        appInitializer)(inject(Store),
        inject(ShopifyAppBridgeService),
        inject(Router),
        inject(ProgramService),
      );
      return initializerFn();
    }),
    provideAppInitializer(() => {
      const initializerFn = registerAppIcons(inject(MatIconRegistry), inject(DomSanitizer));
      return initializerFn();
    }),
    provideAppInitializer(() => {
      const initializerFn = loadInitialTranslations(inject(TranslateService), inject(ShopifyAppBridgeService));
      return initializerFn();
    }),
    provideHttpClient(withInterceptorsFromDi()),
    ...provideTranslateHttpLoader({
      prefix: "./assets/i18n/",
      suffix: `.json?v=${environment.release}`,
    }),
  ]
})
export class AppModule {}
